/*
 * Othr cart drawer controller.
 *
 * - One drawer DOM mounted in layout/theme.liquid, this script binds to it.
 * - Open: header cart button click, .othr-add-to-gear success, custom event "othr:cart:open".
 * - Close: overlay click, close button, Escape key, "Browse the shop" CTA.
 *
 * Performance model — OPTIMISTIC + DEBOUNCED:
 * - Quantity steppers and Remove update the DOM *immediately* (no waiting on the
 *   network), then push the change to Shopify's Ajax Cart API in the background.
 * - Rapid clicks on the same line are coalesced into a single /cart/change.js
 *   request carrying the final quantity (debounced ~300ms).
 * - Lines are patched in place (count + price text) instead of re-rendering the
 *   whole list, so product images never reload / flicker.
 * - After the server settles, the authoritative cart is reconciled silently
 *   (corrects e.g. stock caps) without clobbering edits the user is still making.
 *
 * Money is formatted as `₹N` with Indian thousands separator to match the live site exactly.
 * That formatter assumes a 2-decimal currency (paise). It will still render the correct
 * value for any currency, just without the local symbol or decimal style.
 */
(function () {
  if (window.OthrCart) return; // singleton

  // `?_fd=0&_ab=0&_sc=1` bypasses Shopify's custom-domain redirect
  // (othrvc.myshopify.com → theothr.in). Without it, GET /cart.js
  // 301s to the custom domain and the cross-origin fetch is blocked
  // by CORS, breaking the drawer refresh after add/change.
  var BYPASS = '?_fd=0&_ab=0&_sc=1';
  var ROUTES = {
    cart: '/cart.js' + BYPASS,
    add: '/cart/add.js' + BYPASS,
    change: '/cart/change.js' + BYPASS,
    checkout: '/checkout',
  };

  // How long to wait after the last stepper click before hitting the network.
  var SYNC_DELAY = 300;

  var drawer, overlay, aside, body, foot, title, subtotal, total;
  var lastFocus = null;

  // Debounced background sync state.
  var pending = Object.create(null); // { lineKey: finalQuantity }
  var syncTimer = null;
  var inflight = false;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  // On the dedicated cart page (/cart) the full cart is already on screen, so the
  // slide-in drawer is redundant — suppress it there.
  function isCartPage() {
    return /^\/cart\/?$/.test(window.location.pathname);
  }

  function money(cents) {
    // Shopify stores money as the smallest unit. Indian rupees have 2 decimals (paise).
    var rupees = Math.round((cents || 0) / 100);
    try {
      return '₹' + rupees.toLocaleString('en-IN');
    } catch (e) {
      return '₹' + rupees;
    }
  }

  function pieceCopy(count) {
    if (!count) return 'Empty kit';
    if (count === 1) return '1 piece';
    return count + ' pieces';
  }

  // Bind drawer DOM refs (called after DOMContentLoaded since this script is deferred).
  function findDrawer() {
    drawer = $('[data-cart-drawer]');
    if (!drawer) return false;
    overlay = $('[data-cart-overlay]', drawer);
    aside = drawer.querySelector('.othr-cart-drawer__aside');
    body = $('[data-cart-body]', drawer);
    foot = $('[data-cart-foot]', drawer);
    title = $('[data-cart-title]', drawer);
    subtotal = $('[data-cart-subtotal]', drawer);
    total = $('[data-cart-total]', drawer);
    return true;
  }

  // ---------- rendering ----------

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderEmpty() {
    return ''
      + '<div class="othr-cart-drawer__empty" data-cart-empty>'
      +   '<div class="text-display text-3xl italic">nothing here yet.</div>'
      +   '<p class="max-w-xs text-sm text-muted-foreground">Pick a bar from the shop. The kit fills up here.</p>'
      +   '<a href="/collections/all" class="othr-cart-drawer__empty-cta text-eyebrow" data-cart-browse>'
      +     '<span>Browse the shop</span>'
      +     '<span aria-hidden="true">→</span>'
      +     '<span aria-hidden="true"></span>'
      +   '</a>'
      + '</div>';
  }

  function variantLabel(item) {
    var v = item.variant_title || '';
    if (!v || v === 'Default Title') v = item.properties && item.properties._size ? item.properties._size : '70g';
    return v;
  }

  function itemCategory(item) {
    // Shopify cart line items can carry product_type. Anything more granular needs metafields,
    // which aren't on the cart line — fall back to product_type or "Energy" to match the live site.
    return item.product_type || 'Energy';
  }

  function renderLine(item) {
    var img = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="80" height="80"/>'
      : '';
    // final_price is the per-unit price; cache it so the UI can recompute line
    // totals optimistically without another /cart.js round-trip.
    var unit = item.final_price != null
      ? item.final_price
      : (item.quantity ? Math.round(item.final_line_price / item.quantity) : 0);
    return ''
      + '<li class="othr-cart-line" data-cart-line data-line-key="' + escapeHtml(item.key) + '" data-unit-price="' + unit + '">'
      +   '<div class="othr-cart-line__media">' + img + '</div>'
      +   '<div class="othr-cart-line__main">'
      +     '<div class="othr-cart-line__row">'
      +       '<div class="othr-cart-line__meta">'
      +         '<div class="text-eyebrow opacity-60" style="font-size:0.55rem">'
      +           escapeHtml(itemCategory(item)) + ' · ' + escapeHtml(variantLabel(item))
      +         '</div>'
      +         '<div class="text-display mt-1 text-lg italic">' + escapeHtml((item.product_title || '').toLowerCase()) + '</div>'
      +       '</div>'
      +       '<div class="text-display text-base othr-cart-line__price">' + money(item.final_line_price) + '</div>'
      +     '</div>'
      +     '<div class="othr-cart-line__bottom">'
      +       '<div class="othr-cart-line__qty text-eyebrow">'
      +         '<button type="button" data-cart-qty="dec" aria-label="Decrease quantity">−</button>'
      +         '<span class="othr-cart-line__qty-count" data-cart-qty-count>' + item.quantity + '</span>'
      +         '<button type="button" data-cart-qty="inc" aria-label="Increase quantity">+</button>'
      +       '</div>'
      +       '<button type="button" class="othr-cart-line__remove text-eyebrow" data-cart-remove>Remove</button>'
      +     '</div>'
      +   '</div>'
      + '</li>';
  }

  // Full rebuild from a cart object. Used only when we have no DOM to patch
  // (first paint fallback). Hot paths patch in place instead — see recalcUI/reconcile.
  function render(cart) {
    if (!drawer) return;
    var count = cart.item_count || 0;
    if (title) title.textContent = pieceCopy(count);
    if (count === 0) {
      body.innerHTML = renderEmpty();
      if (foot) foot.setAttribute('hidden', '');
    } else {
      body.innerHTML = '<ul class="othr-cart-drawer__items" data-cart-items>' + cart.items.map(renderLine).join('') + '</ul>';
      if (foot) foot.removeAttribute('hidden');
      if (subtotal) subtotal.textContent = money(cart.total_price);
      if (total) total.textContent = money(cart.total_price);
    }
    $$('.othr-header__cart-count').forEach(function (el) { el.textContent = count; });
    // Refresh discount offer bars after a full re-render
    if (typeof window.discountOffersRefresh === 'function') window.discountOffersRefresh();
  }

  // ---------- in-place DOM helpers (optimistic path) ----------

  function lineEls() { return drawer ? $$('[data-cart-line]', drawer) : []; }

  function findLine(key) {
    return drawer ? drawer.querySelector('[data-cart-line][data-line-key="' + key + '"]') : null;
  }

  function unitPriceOf(li) {
    return parseInt(li.getAttribute('data-unit-price'), 10) || 0;
  }

  function qtyOf(li) {
    var el = li.querySelector('[data-cart-qty-count]');
    return el ? (parseInt(el.textContent, 10) || 0) : 0;
  }

  // Patch a single line's count + price text without touching the rest of the DOM.
  function patchLineQty(li, qty) {
    var countEl = li.querySelector('[data-cart-qty-count]');
    if (countEl) countEl.textContent = qty;
    var priceEl = li.querySelector('.othr-cart-line__price');
    if (priceEl) priceEl.textContent = money(unitPriceOf(li) * qty);
  }

  // Recompute header count, title, subtotal/total from the lines currently in the DOM.
  function recalcUI() {
    if (!drawer) return;
    var els = lineEls();
    var count = 0, subC = 0;
    els.forEach(function (li) {
      var q = qtyOf(li);
      count += q;
      subC += unitPriceOf(li) * q;
    });
    if (title) title.textContent = pieceCopy(count);
    $$('.othr-header__cart-count').forEach(function (el) { el.textContent = count; });
    if (count === 0) {
      body.innerHTML = renderEmpty();
      if (foot) foot.setAttribute('hidden', '');
    } else {
      if (foot) foot.removeAttribute('hidden');
      if (subtotal) subtotal.textContent = money(subC);
      if (total) total.textContent = money(subC);
    }
    // Refresh discount offer bars after optimistic UI update
    if (typeof window.discountOffersRefresh === 'function') window.discountOffersRefresh();
  }

  // Ensure the <ul> list container exists (clears the empty state if present), return it.
  function ensureItemsList() {
    var ul = $('[data-cart-items]', drawer);
    if (!ul) {
      body.innerHTML = '<ul class="othr-cart-drawer__items" data-cart-items></ul>';
      ul = $('[data-cart-items]', drawer);
      if (foot) foot.removeAttribute('hidden');
    }
    return ul;
  }

  // ---------- background sync (debounced) ----------

  function queueSync(key, qty) {
    pending[key] = qty;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(flush, SYNC_DELAY);
  }

  function flush() {
    syncTimer = null;
    if (inflight) return; // a request is running; it will re-flush on completion
    var keys = Object.keys(pending);
    if (!keys.length) return;
    var key = keys[0];
    var qty = pending[key];
    delete pending[key];
    inflight = true;
    fetch(ROUTES.change, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, qty | 0) }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { reconcile(cart); })
      .catch(function (err) { console.error('Cart sync failed', err); })
      .then(function () {
        inflight = false;
        if (Object.keys(pending).length) flush(); // drain remaining edits
      });
  }

  // Bring the DOM in line with the authoritative cart, but never overwrite a line
  // the user is still editing (key present in `pending`).
  function reconcile(cart) {
    if (!drawer || !cart || typeof cart.item_count === 'undefined') return;
    if (cart.item_count === 0 && !Object.keys(pending).length) {
      render(cart);
      return;
    }
    var byKey = Object.create(null);
    (cart.items || []).forEach(function (it) { byKey[it.key] = it; });

    var ul = ensureItemsList();
    lineEls().forEach(function (li) {
      var k = li.getAttribute('data-line-key');
      var it = byKey[k];
      if (!it) {
        if (!(k in pending)) li.remove();
        return;
      }
      if (!(k in pending)) {
        if (it.final_price != null) li.setAttribute('data-unit-price', it.final_price);
        patchLineQty(li, it.quantity);
      }
      delete byKey[k];
    });
    // Any cart lines not yet in the DOM (e.g. just-added) — append them.
    Object.keys(byKey).forEach(function (k) {
      ul.insertAdjacentHTML('beforeend', renderLine(byKey[k]));
    });

    // Authoritative totals (cheaper + handles discounts the client can't see).
    if (!Object.keys(pending).length) {
      if (title) title.textContent = pieceCopy(cart.item_count);
      $$('.othr-header__cart-count').forEach(function (el) { el.textContent = cart.item_count; });
      if (subtotal) subtotal.textContent = money(cart.total_price);
      if (total) total.textContent = money(cart.total_price);
      if (foot) foot.removeAttribute('hidden');
      // Refresh discount offer bars after server reconcile
      if (typeof window.discountOffersRefresh === 'function') window.discountOffersRefresh();
    } else {
      recalcUI();
    }
  }

  // ---------- API calls ----------

  function getCart() {
    return fetch(ROUTES.cart, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function add(variantId, quantity, options) {
    var line = { id: parseInt(variantId, 10), quantity: quantity || 1 };
    if (options) {
      if (options.selling_plan) line.selling_plan = parseInt(options.selling_plan, 10);
      if (options.properties) line.properties = options.properties;
    }
    open(); // open instantly so the drawer feels responsive
    return fetch(ROUTES.add, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [line] }),
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    }).then(function (data) {
      // /cart/add.js returns the added line(s) with up-to-date quantity + price.
      // Patch/insert them immediately, then reconcile totals in the background.
      var items = data && data.items ? data.items : (data ? [data] : []);
      var ul = ensureItemsList();
      items.forEach(function (it) {
        var li = findLine(it.key);
        if (li) {
          if (it.final_price != null) li.setAttribute('data-unit-price', it.final_price);
          patchLineQty(li, it.quantity);
        } else {
          ul.insertAdjacentHTML('beforeend', renderLine(it));
        }
      });
      recalcUI();
      getCart().then(reconcile); // silent correctness pass (discounts, totals)
      return data;
    });
  }

  // Public, optimistic. Updates DOM now, syncs in background.
  function change(lineKey, quantity) {
    var qty = Math.max(0, quantity | 0);
    var li = findLine(lineKey);
    if (li) {
      if (qty <= 0) li.remove();
      else patchLineQty(li, qty);
      recalcUI();
    }
    queueSync(lineKey, qty);
    return Promise.resolve();
  }

  function refresh() {
    return getCart().then(function (cart) { render(cart); return cart; });
  }

  // ---------- open/close ----------

  function open() {
    if (!drawer || isCartPage()) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('othr-cart-open');
    // Focus the dialog for keyboard users.
    if (aside && typeof aside.focus === 'function') {
      setTimeout(function () { aside.focus(); }, 50);
    }
  }

  function close() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('othr-cart-open');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (e) {}
    }
  }

  function toggle() {
    if (!drawer) return;
    if (drawer.getAttribute('aria-hidden') === 'false') close();
    else open();
  }

  // ---------- event wiring ----------

  function handleDrawerClick(e) {
    if (e.target.closest('[data-cart-overlay]')) { close(); return; }
    if (e.target.closest('[data-cart-close]')) { close(); return; }
    if (e.target.closest('[data-cart-browse]')) { close(); return; }

    var qtyBtn = e.target.closest('[data-cart-qty]');
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (qtyBtn || removeBtn) {
      var line = e.target.closest('[data-cart-line]');
      if (!line) return;
      var key = line.getAttribute('data-line-key');
      if (removeBtn) { change(key, 0); return; }
      var count = qtyOf(line);
      var delta = qtyBtn.getAttribute('data-cart-qty') === 'inc' ? 1 : -1;
      change(key, count + delta);
    }
  }

  function handleHeaderCartClick(e) {
    var trigger = e.target.closest('.othr-header__cart, [data-cart-open]');
    if (!trigger) return;
    // Allow Ctrl/Cmd-click to follow the link normally so the user can still
    // get to /cart on a separate tab if they want.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    // On the cart page there's no drawer to open — let the link reload /cart.
    if (isCartPage()) return;
    e.preventDefault();
    open();
  }

  function handleEscape(e) {
    if (e.key === 'Escape' && drawer && drawer.getAttribute('aria-hidden') === 'false') close();
  }

  // ---------- Add-to-gear button delegation ----------
  // Sections (Bestsellers, etc.) render `<button class="othr-add-to-gear" data-variant-id>`.
  // We delegate clicks here so sections don't need inline <script> blocks (which the
  // storefront CSP nonces out anyway).
  function handleAddToGear(e) {
    var btn = e.target.closest('.othr-add-to-gear[data-variant-id]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.busy === '1') return;
    btn.dataset.busy = '1';
    var label = btn.querySelector('[data-cta-label]');
    var original = label ? label.textContent : 'Add to gear';
    if (label) label.textContent = 'Adding…';

    add(btn.dataset.variantId, 1)
      .then(function () {
        if (label) label.textContent = 'Added ✓';
      })
      .catch(function (err) {
        console.error('Add-to-cart failed', err);
        if (label) label.textContent = (err && err.description) ? 'Out of stock' : 'Try again';
      })
      .finally(function () {
        setTimeout(function () {
          btn.dataset.busy = '0';
          if (label) label.textContent = original;
        }, 1600);
      });
  }

  function init() {
    if (!findDrawer()) return;
    drawer.addEventListener('click', handleDrawerClick);
    document.addEventListener('click', handleHeaderCartClick);
    document.addEventListener('click', handleAddToGear);
    document.addEventListener('keydown', handleEscape);
    // Custom events from other sections (e.g. Add to gear).
    document.addEventListener('othr:cart:open', open);
    document.addEventListener('othr:cart:close', close);
    document.addEventListener('othr:cart:add', function (e) {
      var d = e.detail || {};
      if (!d.variantId) return;
      add(d.variantId, d.quantity || 1).then(function () {
        if (d.openOnSuccess === false) close();
      });
    });
  }

  // Public API.
  window.OthrCart = {
    open: open,
    close: close,
    toggle: toggle,
    add: add,
    change: change,
    remove: function (key) { return change(key, 0); },
    refresh: refresh,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
